/**
 * Canadian Payroll Calculator
 * Calculates employee deductions and employer costs for all provinces.
 * Based on CRA T4127 formulas with 2025/2026 rates.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { TylerState, useTylerStateUpdate } from '../utils/TylerState';
import payrollAPI, { PayrollResponse, JournalEntryPreview, PayrollEmployee } from '../services/payroll-api';
import payrollPDFService from '../services/payroll-pdf-service';
import InfoTooltip from './shared/InfoTooltip';
import CollapsibleSection from './CollapsibleSection';
import '../styles/PayrollCalculator.css';

const PROVINCES = [
  { code: 'ON', name: 'Ontario' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'AB', name: 'Alberta' },
  { code: 'QC', name: 'Quebec' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

const PAY_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly (52 pay periods)' },
  { value: 'bi-weekly', label: 'Bi-Weekly (26 pay periods)' },
  { value: 'semi-monthly', label: 'Semi-Monthly (24 pay periods)' },
  { value: 'monthly', label: 'Monthly (12 pay periods)' },
];

const PROVINCE_CODE_TO_NAME: Record<string, string> = {
  'ON': 'Ontario', 'BC': 'British Columbia', 'AB': 'Alberta',
  'QC': 'Quebec', 'MB': 'Manitoba', 'SK': 'Saskatchewan',
  'NS': 'Nova Scotia', 'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador', 'PE': 'Prince Edward Island',
  'NT': 'Northwest Territories', 'NU': 'Nunavut', 'YT': 'Yukon',
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

const OVERTIME_THRESHOLDS: Record<string, number> = {
  'Ontario': 44, 'British Columbia': 40, 'Alberta': 44, 'Quebec': 40,
  'Manitoba': 40, 'Saskatchewan': 40, 'Nova Scotia': 40, 'New Brunswick': 40,
  'Newfoundland and Labrador': 40, 'Prince Edward Island': 40,
  'Northwest Territories': 40, 'Nunavut': 40, 'Yukon': 40,
};

interface PayrollCalculatorProps {
  embedded?: boolean;
  onBeforeCalculate?: (calculateFn: () => void) => void;
}

const PayrollCalculator: React.FC<PayrollCalculatorProps> = ({ embedded, onBeforeCalculate }) => {
  useTylerStateUpdate();

  // Pay type toggle
  const [payType, setPayType] = useState<'salaried' | 'hourly'>('salaried');

  // Salaried input state
  const [grossSalary, setGrossSalary] = useState('75000');

  // Hourly input state
  const [hourlyRate, setHourlyRate] = useState('25.00');
  const [hoursPerWeek, setHoursPerWeek] = useState('40');
  const [overtimeHours, setOvertimeHours] = useState('0');
  const [overtimeRate, setOvertimeRate] = useState('1.5');

  // Shared input state
  const [province, setProvince] = useState('ON');
  const [payFrequency, setPayFrequency] = useState('bi-weekly');
  const [year, setYear] = useState(2026);
  // Pre-tax deductions (per pay period)
  const [rrspDeduction, setRrspDeduction] = useState('0');
  const [pensionDeduction, setPensionDeduction] = useState('0');
  const [unionDues, setUnionDues] = useState('0');

  // Post-tax deductions (frontend only, per pay period)
  const [lifeInsurance, setLifeInsurance] = useState('0');
  const [healthDental, setHealthDental] = useState('0');
  const [otherPostTax, setOtherPostTax] = useState('0');
  const [otherPostTaxLabel, setOtherPostTaxLabel] = useState('Other');

  // Results state
  const [result, setResult] = useState<PayrollResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Journal entry state
  const [journalEntry, setJournalEntry] = useState<JournalEntryPreview | null>(null);
  const [journalLoading, setJournalLoading] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<'paycheck' | 'annual' | 'employer' | 'journal'>('paycheck');

  // Employee save/load state
  const [savedEmployees, setSavedEmployees] = useState<PayrollEmployee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeSaveStatus, setEmployeeSaveStatus] = useState<string>('');

  const isLoggedIn = !!TylerState.user;

  // Load saved employees when logged in
  const loadSavedEmployees = useCallback(async () => {
    if (!isLoggedIn) {
      setSavedEmployees([]);
      return;
    }
    try {
      const employees = await payrollAPI.listEmployees();
      setSavedEmployees(employees);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadSavedEmployees();
  }, [loadSavedEmployees]);

  // Province name-to-code lookup (reverse of code-to-name)
  const PROVINCE_NAME_TO_CODE: Record<string, string> = {};
  Object.entries(PROVINCE_CODE_TO_NAME).forEach(([code, name]) => {
    PROVINCE_NAME_TO_CODE[name] = code;
  });

  // Load employee data into form fields
  const handleLoadEmployee = useCallback((empId: number) => {
    const emp = savedEmployees.find(e => e.id === empId);
    if (!emp) return;

    setSelectedEmployeeId(empId);
    setEmployeeName(emp.name);

    // Set pay type
    if (emp.pay_type === 'hourly') {
      setPayType('hourly');
      setHourlyRate(String(emp.hourly_rate || '25.00'));
      setHoursPerWeek(String(emp.hours_per_period || '40'));
    } else {
      setPayType('salaried');
      setGrossSalary(String(emp.gross_salary || '75000'));
    }

    // Province: the backend stores the full name, we need the code
    const code = PROVINCE_NAME_TO_CODE[emp.province];
    if (code) setProvince(code);

    // Pay frequency
    setPayFrequency(emp.pay_frequency || 'bi-weekly');

    // Pre-tax deductions
    const preTax = emp.pre_tax_deductions || {};
    setRrspDeduction(String(preTax.rrsp || 0));
    setPensionDeduction(String(preTax.pension || 0));
    setUnionDues(String(preTax.union_dues || 0));

    // Post-tax deductions
    const postTax = emp.post_tax_deductions || {};
    setLifeInsurance(String(postTax.life_insurance || 0));
    setHealthDental(String(postTax.health_dental || 0));
    setOtherPostTax(String(postTax.other || 0));
    setOtherPostTaxLabel(postTax.other_label || 'Other');

    // Clear results so user recalculates
    setResult(null);
    setJournalEntry(null);
    setEmployeeSaveStatus('');
  }, [savedEmployees]);

  // Save current form as employee
  const handleSaveEmployee = useCallback(async () => {
    const name = employeeName.trim();
    if (!name) {
      setEmployeeSaveStatus('Please enter an employee name');
      return;
    }

    const employeeData = {
      name,
      pay_type: payType === 'hourly' ? 'hourly' as const : 'salary' as const,
      gross_salary: payType === 'salaried' ? parseFloat(grossSalary) || null : null,
      hourly_rate: payType === 'hourly' ? parseFloat(hourlyRate) || null : null,
      hours_per_period: payType === 'hourly' ? parseFloat(hoursPerWeek) || null : null,
      province: PROVINCE_CODE_TO_NAME[province] || 'Ontario',
      pay_frequency: payFrequency,
      pre_tax_deductions: {
        rrsp: parseFloat(rrspDeduction) || 0,
        pension: parseFloat(pensionDeduction) || 0,
        union_dues: parseFloat(unionDues) || 0,
      },
      post_tax_deductions: {
        life_insurance: parseFloat(lifeInsurance) || 0,
        health_dental: parseFloat(healthDental) || 0,
        other: parseFloat(otherPostTax) || 0,
        other_label: otherPostTaxLabel,
      },
    };

    try {
      if (selectedEmployeeId) {
        await payrollAPI.updateEmployee(selectedEmployeeId, employeeData);
        setEmployeeSaveStatus('Employee updated');
      } else {
        const saved = await payrollAPI.saveEmployee(employeeData);
        setSelectedEmployeeId(saved.id);
        setEmployeeSaveStatus('Employee saved');
      }
      await loadSavedEmployees();
    } catch (err: any) {
      setEmployeeSaveStatus('Save failed: ' + (err.message || 'Unknown error'));
    }
  }, [employeeName, payType, grossSalary, hourlyRate, hoursPerWeek, province, payFrequency, rrspDeduction, pensionDeduction, unionDues, lifeInsurance, healthDental, otherPostTax, otherPostTaxLabel, selectedEmployeeId, loadSavedEmployees]);

  // Delete a saved employee
  const handleDeleteEmployee = useCallback(async () => {
    if (!selectedEmployeeId) return;
    if (!window.confirm('Delete this saved employee?')) return;

    try {
      await payrollAPI.deleteEmployee(selectedEmployeeId);
      setSelectedEmployeeId(null);
      setEmployeeName('');
      setEmployeeSaveStatus('Employee deleted');
      await loadSavedEmployees();
    } catch (err: any) {
      setEmployeeSaveStatus('Delete failed: ' + (err.message || 'Unknown error'));
    }
  }, [selectedEmployeeId, loadSavedEmployees]);

  // Download paystub PDF
  const handleDownloadPaystub = useCallback(() => {
    if (!result) return;
    const today = new Date();
    const payDate = today.toISOString().split('T')[0];

    // Generate a simple pay period label based on frequency
    const periodEnd = new Date(today);
    const periodStart = new Date(today);
    if (result.pay_frequency === 'weekly') {
      periodStart.setDate(periodStart.getDate() - 6);
    } else if (result.pay_frequency === 'bi-weekly') {
      periodStart.setDate(periodStart.getDate() - 13);
    } else if (result.pay_frequency === 'semi-monthly') {
      periodStart.setDate(1);
      if (today.getDate() <= 15) {
        periodEnd.setDate(15);
      } else {
        periodStart.setDate(16);
      }
    } else {
      periodStart.setDate(1);
    }

    const formatDate = (d: Date) => d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    const payPeriodLabel = formatDate(periodStart) + ' - ' + formatDate(periodEnd);

    payrollPDFService.generatePaystubPDF(result, {
      employeeName: employeeName.trim() || 'Employee',
      payPeriodLabel,
      payDate,
      postTaxDeductions: {
        lifeInsurance: parseFloat(lifeInsurance) || 0,
        healthDental: parseFloat(healthDental) || 0,
        other: parseFloat(otherPostTax) || 0,
        otherLabel: otherPostTaxLabel,
      },
    });
  }, [result, employeeName, lifeInsurance, healthDental, otherPostTax, otherPostTaxLabel]);

  const provinceName = PROVINCE_CODE_TO_NAME[province] || 'Ontario';
  const overtimeThreshold = OVERTIME_THRESHOLDS[provinceName] || 40;

  const buildPreTaxDeductions = () => {
    const rrsp = parseFloat(rrspDeduction) || 0;
    const pension = parseFloat(pensionDeduction) || 0;
    const dues = parseFloat(unionDues) || 0;
    if (rrsp === 0 && pension === 0 && dues === 0) return undefined;
    return { rrsp, pension, union_dues: dues };
  };

  const handleCalculate = useCallback(async () => {
    setLoading(true);
    setError('');
    setJournalEntry(null);

    const pre_tax_deductions = buildPreTaxDeductions();

    try {
      let data: PayrollResponse;

      if (payType === 'hourly') {
        const rate = parseFloat(hourlyRate);
        if (isNaN(rate) || rate <= 0) {
          setError('Please enter a valid hourly rate');
          setLoading(false);
          return;
        }
        data = await payrollAPI.calculateHourly({
          hourly_rate: rate,
          hours_per_week: parseFloat(hoursPerWeek) || 40,
          overtime_hours: parseFloat(overtimeHours) || 0,
          overtime_rate: parseFloat(overtimeRate) || 1.5,
          province: provinceName,
          pay_frequency: payFrequency,
          year,
          num_employees: 1,
          pre_tax_deductions,
        });
      } else {
        const salary = parseFloat(grossSalary);
        if (isNaN(salary) || salary <= 0) {
          setError('Please enter a valid gross salary');
          setLoading(false);
          return;
        }
        data = await payrollAPI.calculate({
          gross_salary: salary,
          province: provinceName,
          pay_frequency: payFrequency,
          year,
          num_employees: 1,
          pre_tax_deductions,
        });
      }

      setResult(data);
      setActiveTab('paycheck');
    } catch (err: any) {
      setError(err.message || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  }, [payType, grossSalary, hourlyRate, hoursPerWeek, overtimeHours, overtimeRate, province, provinceName, payFrequency, year, rrspDeduction, pensionDeduction, unionDues]);

  const handleGenerateJournal = useCallback(async () => {
    if (!result) return;
    setJournalLoading(true);
    try {
      // Use placeholder account IDs -- user would map these in real accounting
      const accounts = {
        wages_expense: null,
        cpp_expense: null,
        ei_expense: null,
        cpp_payable: null,
        ei_payable: null,
        federal_tax_payable: null,
        provincial_tax_payable: null,
        cash: null,
        qpip_expense: null,
        qpip_payable: null,
      };
      const entry = await payrollAPI.generateJournalEntry(result, accounts);
      setJournalEntry(entry);
      setActiveTab('journal');
    } catch (err: any) {
      setError(err.message || 'Failed to generate journal entry');
    } finally {
      setJournalLoading(false);
    }
  }, [result]);

  const pensionLabel = result?.pension_label || 'CPP';
  const isQuebec = province === 'QC';

  return (
    <div className={embedded ? '' : 'payroll-calculator'}>
      {!embedded && (
        <div className="calculator-header">
          <h1>Canadian Payroll Calculator</h1>
          <p>Calculate employee deductions and employer costs for all provinces</p>
        </div>
      )}

      <div className="calculator-content">
        {/* Input Panel */}
        <div className="tax-input-section">
          <h2>Employee Details</h2>

          {/* Employee Name + Save/Load (logged-in users only) */}
          {isLoggedIn && (
            <div style={{ marginBottom: '16px' }}>
              <div className="input-grid">
                <div className="input-group">
                  <label>Employee Name</label>
                  <input
                    type="text"
                    value={employeeName}
                    onChange={(e) => { setEmployeeName(e.target.value); setEmployeeSaveStatus(''); }}
                    placeholder="e.g. John Smith"
                    maxLength={255}
                  />
                </div>
                {savedEmployees.length > 0 && (
                  <div className="input-group">
                    <label>Load Saved Employee</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={selectedEmployeeId || ''}
                        onChange={(e) => {
                          const id = parseInt(e.target.value);
                          if (id) handleLoadEmployee(id);
                          else {
                            setSelectedEmployeeId(null);
                            setEmployeeName('');
                          }
                        }}
                        style={{ flex: 1 }}
                      >
                        <option value="">-- New Employee --</option>
                        {savedEmployees.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name} ({emp.pay_type === 'hourly' ? 'Hourly' : 'Salaried'})
                          </option>
                        ))}
                      </select>
                      {selectedEmployeeId && (
                        <button
                          onClick={handleDeleteEmployee}
                          style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                          title="Delete saved employee"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {employeeSaveStatus && (
                <div style={{ fontSize: '0.85rem', color: employeeSaveStatus.startsWith('Save failed') || employeeSaveStatus.startsWith('Delete failed') ? '#dc2626' : '#059669', marginTop: '4px' }}>
                  {employeeSaveStatus}
                </div>
              )}
            </div>
          )}

          {/* Pay Type Toggle */}
          <div className="pay-type-toggle">
            <button
              className={`toggle-btn ${payType === 'salaried' ? 'active' : ''}`}
              onClick={() => { setPayType('salaried'); setResult(null); setJournalEntry(null); }}
            >
              Salaried
            </button>
            <button
              className={`toggle-btn ${payType === 'hourly' ? 'active' : ''}`}
              onClick={() => { setPayType('hourly'); setResult(null); setJournalEntry(null); }}
            >
              Hourly
            </button>
          </div>

          <div className="input-grid">
            {payType === 'salaried' ? (
              <div className="input-group">
                <label>
                  Gross Annual Salary
                  <InfoTooltip content="Total annual salary before any deductions" />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={grossSalary}
                    onChange={(e) => { setGrossSalary(e.target.value); setResult(null); }}
                    placeholder="75,000"
                    min="0"
                    step="1000"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="input-group">
                  <label>
                    Hourly Rate
                    <InfoTooltip content="Regular hourly wage before overtime" />
                  </label>
                  <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input
                      type="number"
                      value={hourlyRate}
                      onChange={(e) => { setHourlyRate(e.target.value); setResult(null); }}
                      placeholder="25.00"
                      min="0"
                      step="0.25"
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>
                    Hours per Week
                    <InfoTooltip content={`Regular hours worked per week. Overtime threshold in ${provinceName}: ${overtimeThreshold}h/week`} />
                  </label>
                  <input
                    type="number"
                    value={hoursPerWeek}
                    onChange={(e) => { setHoursPerWeek(e.target.value); setResult(null); }}
                    placeholder="40"
                    min="0"
                    max="168"
                    step="1"
                  />
                </div>

                <div className="input-group">
                  <label>
                    Additional Overtime Hours / Week
                    <InfoTooltip content="Extra overtime hours beyond what is auto-calculated from hours exceeding the provincial threshold." />
                  </label>
                  <input
                    type="number"
                    value={overtimeHours}
                    onChange={(e) => { setOvertimeHours(e.target.value); setResult(null); }}
                    placeholder="0"
                    min="0"
                    step="0.5"
                  />
                </div>

                <div className="input-group">
                  <label>
                    Overtime Rate
                    <InfoTooltip content="Overtime pay multiplier. Standard is 1.5x (time and a half). Some roles pay 2x (double time)." />
                  </label>
                  <select
                    value={overtimeRate}
                    onChange={(e) => { setOvertimeRate(e.target.value); setResult(null); }}
                  >
                    <option value="1.5">1.5x (Time and a half)</option>
                    <option value="2">2x (Double time)</option>
                  </select>
                </div>

                {parseFloat(hoursPerWeek) > overtimeThreshold && (
                  <div className="overtime-notice">
                    {parseFloat(hoursPerWeek) - overtimeThreshold}h/week auto-calculated as overtime
                    ({provinceName} threshold: {overtimeThreshold}h)
                  </div>
                )}
              </>
            )}

            <div className="input-group">
              <label>
                Province
                <InfoTooltip content="Province of employment determines provincial tax rates. Quebec uses QPP instead of CPP and has QPIP." />
              </label>
              <select
                value={province}
                onChange={(e) => { setProvince(e.target.value); setResult(null); }}
              >
                {PROVINCES.map(p => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>
                Pay Frequency
                <InfoTooltip content="How often the employee is paid. Affects per-paycheck deduction amounts." />
              </label>
              <select
                value={payFrequency}
                onChange={(e) => { setPayFrequency(e.target.value); setResult(null); }}
              >
                {PAY_FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Tax Year</label>
              <select
                value={year}
                onChange={(e) => { setYear(parseInt(e.target.value)); setResult(null); }}
              >
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
              </select>
            </div>

          </div>

          {/* Pre-Tax Deductions */}
          <CollapsibleSection title="Pre-Tax Deductions" defaultOpen={false} icon="📋">
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 12 }}>
              Per pay period amounts. Reduces taxable income (CPP/EI still based on gross).
            </p>
            <div className="input-grid">
              <div className="input-group">
                <label>
                  RRSP Contribution
                  <InfoTooltip content="Registered Retirement Savings Plan contribution per pay period. Reduces taxable income." />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={rrspDeduction}
                    onChange={(e) => { setRrspDeduction(e.target.value); setResult(null); }}
                    placeholder="0"
                    min="0"
                    step="25"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>
                  Pension Plan (RPP)
                  <InfoTooltip content="Registered Pension Plan contribution per pay period. Reduces taxable income and earns 15% tax credit." />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={pensionDeduction}
                    onChange={(e) => { setPensionDeduction(e.target.value); setResult(null); }}
                    placeholder="0"
                    min="0"
                    step="25"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>
                  Union Dues
                  <InfoTooltip content="Union or professional dues per pay period. Reduces taxable income and earns 15% tax credit." />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={unionDues}
                    onChange={(e) => { setUnionDues(e.target.value); setResult(null); }}
                    placeholder="0"
                    min="0"
                    step="5"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Post-Tax Deductions */}
          <CollapsibleSection title="Post-Tax Deductions" defaultOpen={false} icon="🏥">
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 12 }}>
              Per pay period amounts. Reduces take-home pay only (no tax impact).
            </p>
            <div className="input-grid">
              <div className="input-group">
                <label>Group Life Insurance</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={lifeInsurance}
                    onChange={(e) => setLifeInsurance(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="5"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>Health / Dental Benefits</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={healthDental}
                    onChange={(e) => setHealthDental(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="5"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>
                  <input
                    type="text"
                    value={otherPostTaxLabel}
                    onChange={(e) => setOtherPostTaxLabel(e.target.value)}
                    style={{ border: 'none', fontWeight: 600, padding: 0, width: '100%', background: 'transparent' }}
                    placeholder="Other"
                  />
                </label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    value={otherPostTax}
                    onChange={(e) => setOtherPostTax(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="5"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <button
            className="calculate-btn"
            onClick={() => {
              if (onBeforeCalculate) {
                onBeforeCalculate(handleCalculate);
              } else {
                handleCalculate();
              }
            }}
            disabled={loading}
          >
            {loading ? 'Calculating...' : 'Calculate Payroll'}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>

        {/* Results Panel */}
        {result && (() => {
          const postTaxTotal = (parseFloat(lifeInsurance) || 0) + (parseFloat(healthDental) || 0) + (parseFloat(otherPostTax) || 0);
          const takeHome = result.per_period.net_pay - postTaxTotal;
          const hasPostTax = postTaxTotal > 0;

          return (
          <div className="payroll-results">
            {/* Big Numbers */}
            <div className="big-numbers">
              <div className="big-number-card income">
                <span className="label">Gross Pay / Period</span>
                <span className="value">{formatCurrency(result.per_period.gross_pay)}</span>
              </div>
              <div className="big-number-card deductions">
                <span className="label">Deductions / Period</span>
                <span className="value negative">{formatCurrency(result.per_period.total_deductions)}</span>
              </div>
              <div className="big-number-card net">
                <span className="label">{hasPostTax ? 'Net Pay (before voluntary)' : 'Net Pay / Period'}</span>
                <span className="value">{formatCurrency(result.per_period.net_pay)}</span>
              </div>
              {hasPostTax && (
                <div className="big-number-card net" style={{ borderTop: '2px dashed #e2e8f0' }}>
                  <span className="label" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Less: Life Insurance {formatCurrency(parseFloat(lifeInsurance) || 0)}
                    {(parseFloat(healthDental) || 0) > 0 && ` | Health/Dental ${formatCurrency(parseFloat(healthDental) || 0)}`}
                    {(parseFloat(otherPostTax) || 0) > 0 && ` | ${otherPostTaxLabel} ${formatCurrency(parseFloat(otherPostTax) || 0)}`}
                  </span>
                  <span className="label" style={{ fontWeight: 700, marginTop: 6 }}>Actual Take-Home Pay</span>
                  <span className="value" style={{ color: '#059669' }}>{formatCurrency(takeHome)}</span>
                </div>
              )}
            </div>

            {/* Hourly Breakdown (shown only for hourly pay type) */}
            {result.hourly_details && (
              <div className="hourly-breakdown-card">
                <h3>Hourly Pay Breakdown</h3>
                <div className="hourly-breakdown-grid">
                  <div className="hourly-stat">
                    <span className="stat-label">Hourly Rate</span>
                    <span className="stat-value">{formatCurrency(result.hourly_details.hourly_rate)}/hr</span>
                  </div>
                  <div className="hourly-stat">
                    <span className="stat-label">Regular Hours</span>
                    <span className="stat-value">{result.hourly_details.regular_hours}h/wk</span>
                  </div>
                  <div className="hourly-stat">
                    <span className="stat-label">Overtime Hours</span>
                    <span className="stat-value">{result.hourly_details.overtime_hours_per_week}h/wk</span>
                    <span className="stat-hint">@ {result.hourly_details.overtime_rate_multiplier}x rate</span>
                  </div>
                  <div className="hourly-stat">
                    <span className="stat-label">OT Threshold</span>
                    <span className="stat-value">{result.hourly_details.overtime_threshold}h/wk</span>
                    <span className="stat-hint">{result.province}</span>
                  </div>
                  <div className="hourly-stat">
                    <span className="stat-label">Regular Weekly Pay</span>
                    <span className="stat-value">{formatCurrency(result.hourly_details.regular_weekly_pay)}</span>
                  </div>
                  <div className="hourly-stat">
                    <span className="stat-label">Overtime Weekly Pay</span>
                    <span className="stat-value" style={{ color: result.hourly_details.overtime_weekly_pay > 0 ? '#059669' : undefined }}>
                      {formatCurrency(result.hourly_details.overtime_weekly_pay)}
                    </span>
                  </div>
                  <div className="hourly-stat highlight">
                    <span className="stat-label">Total Weekly Pay</span>
                    <span className="stat-value">{formatCurrency(result.hourly_details.total_weekly_pay)}</span>
                  </div>
                  <div className="hourly-stat highlight">
                    <span className="stat-label">Annual Gross Pay</span>
                    <span className="stat-value">{formatCurrency(result.hourly_details.annual_gross_pay)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Effective Rate Bar */}
            <div className="deductions-breakdown">
              <h3>Deductions Breakdown</h3>
              <div className="breakdown-bars">
                <div className="breakdown-item">
                  <div className="bar-label">
                    <span>Federal Tax</span>
                    <span>{formatPercent(result.effective_rates.federal_rate)}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar federal" style={{ width: `${Math.min(result.effective_rates.federal_rate * 2, 100)}%` }} />
                  </div>
                </div>
                <div className="breakdown-item">
                  <div className="bar-label">
                    <span>Provincial Tax</span>
                    <span>{formatPercent(result.effective_rates.provincial_rate)}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar provincial" style={{ width: `${Math.min(result.effective_rates.provincial_rate * 2, 100)}%` }} />
                  </div>
                </div>
                <div className="breakdown-item">
                  <div className="bar-label">
                    <span>{pensionLabel} + {pensionLabel}2</span>
                    <span>{formatPercent(result.effective_rates.pension_rate)}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar cpp" style={{ width: `${Math.min(result.effective_rates.pension_rate * 2, 100)}%` }} />
                  </div>
                </div>
                <div className="breakdown-item">
                  <div className="bar-label">
                    <span>EI{isQuebec ? ' + QPIP' : ''}</span>
                    <span>{formatPercent(result.effective_rates.ei_rate)}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar ei" style={{ width: `${Math.min(result.effective_rates.ei_rate * 2, 100)}%` }} />
                  </div>
                </div>
                <div className="breakdown-item take-home">
                  <div className="bar-label">
                    <span>Take Home</span>
                    <span>{formatPercent(100 - result.effective_rates.total_tax_rate)}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar net" style={{ width: `${100 - result.effective_rates.total_tax_rate}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="results-panel">
              <div className="results-tabs">
                <button
                  className={`tab ${activeTab === 'paycheck' ? 'active' : ''}`}
                  onClick={() => setActiveTab('paycheck')}
                >
                  Per Paycheck
                </button>
                <button
                  className={`tab ${activeTab === 'annual' ? 'active' : ''}`}
                  onClick={() => setActiveTab('annual')}
                >
                  Annual Summary
                </button>
                <button
                  className={`tab ${activeTab === 'employer' ? 'active' : ''}`}
                  onClick={() => setActiveTab('employer')}
                >
                  Employer Costs
                </button>
                <button
                  className={`tab ${activeTab === 'journal' ? 'active' : ''}`}
                  onClick={() => {
                    if (!journalEntry) { handleGenerateJournal(); }
                    else { setActiveTab('journal'); }
                  }}
                  disabled={journalLoading}
                >
                  {journalLoading ? 'Loading...' : 'Journal Entry'}
                </button>
              </div>

              <div className="results-content">
                {activeTab === 'paycheck' && renderPaycheckTab(result, pensionLabel, isQuebec)}
                {activeTab === 'annual' && renderAnnualTab(result, pensionLabel, isQuebec)}
                {activeTab === 'employer' && renderEmployerTab(result, pensionLabel, isQuebec)}
                {activeTab === 'journal' && renderJournalTab(journalEntry, result)}
              </div>
            </div>

            {/* Action Buttons: Save Employee + Download Paystub */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {isLoggedIn && (
                <button
                  onClick={handleSaveEmployee}
                  style={{
                    padding: '10px 20px',
                    background: '#1e3a5f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  {selectedEmployeeId ? 'Update Employee' : 'Save Employee'}
                </button>
              )}
              <button
                onClick={handleDownloadPaystub}
                style={{
                  padding: '10px 20px',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Download Paystub (PDF)
              </button>
            </div>

            {/* Disclaimer */}
            <div className="payroll-disclaimer">
              <strong>Disclaimer</strong>
              This payroll calculator provides estimates based on published CRA rates and T4127
              formulas. Calculations are for informational purposes only and should not be relied
              upon as a substitute for professional payroll services. The employer is responsible
              for verifying all deductions and remittances with CRA. Larry's Help assumes no
              liability for payroll calculation errors.
              {isQuebec && (
                <p style={{ marginTop: '8px' }}>
                  Quebec employees use QPP (Quebec Pension Plan) instead of CPP, and pay reduced
                  EI premiums plus QPIP (Quebec Parental Insurance Plan). Rates shown reflect
                  Quebec-specific calculations.
                </p>
              )}
            </div>
          </div>
          );
        })()}

        {/* Empty State */}
        {!result && !loading && (
          <div className="results-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💰</div>
              <h3 style={{ color: '#6b7280', marginBottom: '8px' }}>Enter salary details</h3>
              <p>Calculate employee deductions, net pay, and total employer costs</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ TAB RENDERERS ============

function renderPaycheckTab(result: PayrollResponse, pensionLabel: string, isQuebec: boolean) {
  const pp = result.per_period;
  const freq = result.pay_frequency.replace('-', '-');
  const freqLabel = freq === 'bi-weekly' ? 'Bi-Weekly' : freq === 'semi-monthly' ? 'Semi-Monthly' : freq.charAt(0).toUpperCase() + freq.slice(1);

  return (
    <div>
      <table className="paycheck-stub">
        <thead>
          <tr>
            <th>Description</th>
            <th style={{ textAlign: 'right' }}>{freqLabel} Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Gross Pay</strong></td>
            <td style={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(pp.gross_pay)}</td>
          </tr>

          <tr className="section-row"><td colSpan={2}>Employee Deductions</td></tr>
          <tr>
            <td>Federal Income Tax</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(pp.federal_tax)}</td>
          </tr>
          <tr>
            <td>Provincial Income Tax</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(pp.provincial_tax)}</td>
          </tr>
          <tr>
            <td>{pensionLabel}</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(pp.pension_employee)}</td>
          </tr>
          {pp.pension2_employee > 0 && (
            <tr>
              <td>{pensionLabel}2</td>
              <td style={{ color: '#dc2626' }}>{formatCurrency(pp.pension2_employee)}</td>
            </tr>
          )}
          <tr>
            <td>Employment Insurance (EI)</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(pp.ei_employee)}</td>
          </tr>
          {isQuebec && pp.qpip_employee > 0 && (
            <tr>
              <td>QPIP</td>
              <td style={{ color: '#dc2626' }}>{formatCurrency(pp.qpip_employee)}</td>
            </tr>
          )}

          <tr className="subtotal-row">
            <td>Total Deductions</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(pp.total_deductions)}</td>
          </tr>

          <tr className="net-row">
            <td>Net Pay (Take Home)</td>
            <td style={{ color: '#059669' }}>{formatCurrency(pp.net_pay)}</td>
          </tr>

          <tr className="section-row"><td colSpan={2}>Employer Contributions</td></tr>
          <tr>
            <td>{pensionLabel} (Employer Match)</td>
            <td>{formatCurrency(pp.pension_employer)}</td>
          </tr>
          {pp.pension2_employer > 0 && (
            <tr>
              <td>{pensionLabel}2 (Employer Match)</td>
              <td>{formatCurrency(pp.pension2_employer)}</td>
            </tr>
          )}
          <tr>
            <td>EI (Employer — 1.4x){isQuebec ? ' + QPIP' : ''}</td>
            <td>{formatCurrency(pp.ei_employer + (isQuebec ? pp.qpip_employer : 0))}</td>
          </tr>

          <tr className="employer-row">
            <td>Total Employer Contributions</td>
            <td>{formatCurrency(pp.total_employer_contributions)}</td>
          </tr>

          <tr className="total-cost-row">
            <td>Total Cost to Employer</td>
            <td>{formatCurrency(pp.total_cost_to_employer)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function renderAnnualTab(result: PayrollResponse, pensionLabel: string, isQuebec: boolean) {
  const a = result.annual;

  return (
    <div>
      <table className="paycheck-stub">
        <thead>
          <tr>
            <th>Description</th>
            <th style={{ textAlign: 'right' }}>Annual Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Gross Salary</strong></td>
            <td style={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(a.gross_salary)}</td>
          </tr>

          <tr className="section-row"><td colSpan={2}>Employee Deductions</td></tr>
          <tr>
            <td>Federal Income Tax</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(a.federal_tax)}</td>
          </tr>
          <tr>
            <td>Provincial Income Tax</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(a.provincial_tax)}</td>
          </tr>
          <tr>
            <td>{pensionLabel}</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(a.pension_base_employee)}</td>
          </tr>
          {a.pension2_employee > 0 && (
            <tr>
              <td>{pensionLabel}2</td>
              <td style={{ color: '#dc2626' }}>{formatCurrency(a.pension2_employee)}</td>
            </tr>
          )}
          <tr>
            <td>Employment Insurance (EI)</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(a.ei_employee)}</td>
          </tr>
          {isQuebec && a.qpip_employee > 0 && (
            <tr>
              <td>QPIP</td>
              <td style={{ color: '#dc2626' }}>{formatCurrency(a.qpip_employee)}</td>
            </tr>
          )}

          <tr className="subtotal-row">
            <td>Total Deductions</td>
            <td style={{ color: '#dc2626' }}>{formatCurrency(a.total_employee_deductions)}</td>
          </tr>

          <tr className="net-row">
            <td>Net Annual Pay (Take Home)</td>
            <td style={{ color: '#059669' }}>{formatCurrency(a.net_pay)}</td>
          </tr>

          <tr className="section-row"><td colSpan={2}>Employer Annual Contributions</td></tr>
          <tr>
            <td>{pensionLabel} (Employer)</td>
            <td>{formatCurrency(a.pension_base_employer)}</td>
          </tr>
          {a.pension2_employer > 0 && (
            <tr>
              <td>{pensionLabel}2 (Employer)</td>
              <td>{formatCurrency(a.pension2_employer)}</td>
            </tr>
          )}
          <tr>
            <td>EI (Employer){isQuebec ? ' + QPIP' : ''}</td>
            <td>{formatCurrency(a.ei_employer + (isQuebec ? a.qpip_employer : 0))}</td>
          </tr>

          <tr className="employer-row">
            <td>Total Employer Contributions</td>
            <td>{formatCurrency(a.total_employer_contributions)}</td>
          </tr>

          <tr className="total-cost-row">
            <td>Total Annual Cost to Employer</td>
            <td>{formatCurrency(a.total_cost_to_employer)}</td>
          </tr>
        </tbody>
      </table>

      {/* Rates Info */}
      <div className="rates-card" style={{ marginTop: '20px' }}>
        <h3>{result.year} Rates Applied</h3>
        <div className="rates-grid">
          <div className="rate-item">
            <span className="rate-label">{pensionLabel} Rate</span>
            <span className="rate-value">{(result.rates_info.pension_rate * 100).toFixed(2)}%</span>
            <span className="rate-hint">YMPE: {formatCurrency(result.rates_info.pension_ympe)}</span>
          </div>
          <div className="rate-item">
            <span className="rate-label">{pensionLabel}2 Rate</span>
            <span className="rate-value">{(result.rates_info.pension2_rate * 100).toFixed(1)}%</span>
            <span className="rate-hint">YAMPE: {formatCurrency(result.rates_info.pension_yampe)}</span>
          </div>
          <div className="rate-item">
            <span className="rate-label">EI Employee</span>
            <span className="rate-value">{(result.rates_info.ei_employee_rate * 100).toFixed(2)}%</span>
            <span className="rate-hint">Max: {formatCurrency(result.rates_info.ei_max_insurable)}</span>
          </div>
          <div className="rate-item">
            <span className="rate-label">EI Employer</span>
            <span className="rate-value">{(result.rates_info.ei_employer_rate * 100).toFixed(2)}%</span>
            <span className="rate-hint">1.4x employee rate</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderEmployerTab(result: PayrollResponse, pensionLabel: string, isQuebec: boolean) {
  const a = result.annual;

  return (
    <div>
      <div className="employer-highlight">
        <h3>Employer Cost Summary</h3>
        <div className="employer-grid">
          <div className="employer-stat">
            <span className="stat-label">Employee Salary</span>
            <span className="stat-value">{formatCurrency(a.gross_salary)}</span>
          </div>
          <div className="employer-stat">
            <span className="stat-label">{pensionLabel} + {pensionLabel}2</span>
            <span className="stat-value">{formatCurrency(a.total_pension_employer)}</span>
            <span className="stat-hint">Matching employee contribution</span>
          </div>
          <div className="employer-stat">
            <span className="stat-label">EI{isQuebec ? ' + QPIP' : ''}</span>
            <span className="stat-value">{formatCurrency(a.ei_employer + (isQuebec ? a.qpip_employer : 0))}</span>
            <span className="stat-hint">{isQuebec ? 'Reduced EI + QPIP' : '1.4x employee premium'}</span>
          </div>
          <div className="employer-stat">
            <span className="stat-label">Employer Burden Rate</span>
            <span className="stat-value">{formatPercent(result.effective_rates.employer_burden_rate)}</span>
            <span className="stat-hint">Above salary cost</span>
          </div>
        </div>
      </div>

      {/* Total Cost */}
      <div style={{
        marginTop: '20px',
        padding: '24px',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        borderRadius: '12px',
        color: 'white',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '0.9rem', opacity: 0.85, marginBottom: '8px' }}>
          Total Annual Cost to Employer (per employee)
        </div>
        <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>
          {formatCurrency(a.total_cost_to_employer)}
        </div>
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '0.9rem', opacity: 0.85 }}>
          <span>Salary: {formatCurrency(a.gross_salary)}</span>
          <span>+ Contributions: {formatCurrency(a.total_employer_contributions)}</span>
        </div>
      </div>

    </div>
  );
}

function renderJournalTab(journalEntry: JournalEntryPreview | null, result: PayrollResponse | null) {
  if (!journalEntry) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
        <p>Generate a journal entry from the payroll calculation above.</p>
      </div>
    );
  }

  const freqLabel = result?.pay_frequency === 'bi-weekly' ? 'Bi-Weekly'
    : result?.pay_frequency === 'semi-monthly' ? 'Semi-Monthly'
    : (result?.pay_frequency || 'Period').charAt(0).toUpperCase() + (result?.pay_frequency || 'period').slice(1);

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ color: '#1e3a5f', marginBottom: '4px' }}>Payroll Journal Entry ({freqLabel})</h3>
        <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          Double-entry bookkeeping preview. Map to your chart of accounts before posting.
        </p>
      </div>

      <table className="paycheck-stub">
        <thead>
          <tr>
            <th>Account</th>
            <th style={{ textAlign: 'right' }}>Debit</th>
            <th style={{ textAlign: 'right' }}>Credit</th>
          </tr>
        </thead>
        <tbody>
          {journalEntry.lines.map((line) => (
            <tr key={line.line_number}>
              <td>
                <div>
                  <span style={{ fontWeight: line.debit_amount > 0 ? 600 : 400, paddingLeft: line.credit_amount > 0 ? '20px' : '0' }}>
                    {line.account_label}
                  </span>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', paddingLeft: line.credit_amount > 0 ? '20px' : '0' }}>
                    {line.description}
                  </div>
                </div>
              </td>
              <td style={{ textAlign: 'right', fontFamily: "'Monaco', 'Menlo', monospace" }}>
                {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : ''}
              </td>
              <td style={{ textAlign: 'right', fontFamily: "'Monaco', 'Menlo', monospace" }}>
                {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : ''}
              </td>
            </tr>
          ))}

          <tr className="subtotal-row">
            <td><strong>Totals</strong></td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontFamily: "'Monaco', 'Menlo', monospace" }}>
              {formatCurrency(journalEntry.total_debits)}
            </td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontFamily: "'Monaco', 'Menlo', monospace" }}>
              {formatCurrency(journalEntry.total_credits)}
            </td>
          </tr>
        </tbody>
      </table>

      {journalEntry.is_balanced ? (
        <div style={{ marginTop: '12px', padding: '10px 16px', background: '#dcfce7', borderRadius: '8px', color: '#166534', fontSize: '0.9rem', fontWeight: 600 }}>
          Balanced -- Debits equal Credits
        </div>
      ) : (
        <div style={{ marginTop: '12px', padding: '10px 16px', background: '#fef2f2', borderRadius: '8px', color: '#991b1b', fontSize: '0.9rem', fontWeight: 600 }}>
          Warning: Entry is not balanced (difference: {formatCurrency(Math.abs(journalEntry.total_debits - journalEntry.total_credits))})
        </div>
      )}

      <div style={{ marginTop: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
        This is a preview only. To post this entry, map each line to an account in your Chart of Accounts
        and submit through the Accounting module.
      </div>
    </div>
  );
}

export default PayrollCalculator;
